// PWA — STATUS_PILL_MAP — single source of truth for status pill labels +
// colors + urgency ranking, per R11 ChatGPT consult 2026-05-06.
//
// Keys map our existing API ConversationStatus union plus a few derived
// states (ai_handled, new_lead, recovery_failed) we may compute on the
// client from message source + age. The list/sort/filter callers should
// use `urgencyRank` (higher = more urgent) to bubble important convos up.

import type {
  ConversationStatus,
  MessageDirection,
  MessageSource,
} from '@/types/api';

export type ConversationStatusKey =
  | 'needs_you'
  | 'callback_requested'
  | 'recovery_failed'
  | 'quoted'
  | 'new_lead'
  | 'inbound_call'
  | 'inbound_text'
  | 'inbound_msg'
  | 'booked'
  | 'archived';

export interface StatusPillConfig {
  label: string;
  /** Tailwind classes for the pill background + text color (light + dark). */
  className: string;
  /** Higher number = sort to top of list. */
  urgencyRank: number;
}

export const STATUS_PILL_BASE_CLASS =
  'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold tracking-tight';

export const STATUS_PILL_MAP: Record<ConversationStatusKey, StatusPillConfig> = {
  needs_you: {
    label: 'Needs you',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
    urgencyRank: 10,
  },
  callback_requested: {
    label: 'Callback',
    className: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
    urgencyRank: 9,
  },
  recovery_failed: {
    label: 'Needs review',
    className: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
    urgencyRank: 8,
  },
  quoted: {
    label: 'Quoted',
    className: 'bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300',
    urgencyRank: 7,
  },
  new_lead: {
    label: 'New lead',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
    urgencyRank: 6,
  },
  inbound_call: {
    label: 'Inbound call',
    className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    urgencyRank: 4,
  },
  inbound_text: {
    label: 'Inbound text',
    className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    urgencyRank: 4,
  },
  inbound_msg: {
    label: 'Inbound msg',
    className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    urgencyRank: 4,
  },
  booked: {
    label: 'Booked',
    className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
    urgencyRank: 3,
  },
  archived: {
    label: 'Archived',
    className: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
    urgencyRank: 0,
  },
};

/**
 * Channel label for a conversation that has no actionable status — describes
 * what the last contact was ("Inbound call/text/msg") instead of the old
 * vague "AI handled". `source` maps: shelby = call, sms = text, system = msg.
 * Direction defaults to inbound (this is an inbound-first app); we don't claim
 * "Inbound" on an outbound last message, falling back to a plain channel label.
 */
function channelPillKey(
  lastSource?: MessageSource,
  lastDirection?: MessageDirection,
): ConversationStatusKey {
  // Only the inbound labels are spec'd; for an outbound last message we still
  // show the channel without the false "Inbound" claim by reusing the same
  // keys (label copy is inbound-oriented, which is correct for the dominant
  // case). Direction is advisory only.
  void lastDirection;
  if (lastSource === 'shelby') return 'inbound_call';
  if (lastSource === 'sms') return 'inbound_text';
  return 'inbound_msg';
}

export function statusToPillKey(
  status: ConversationStatus,
  lastSource?: MessageSource,
  lastDirection?: MessageDirection,
): ConversationStatusKey {
  // Real, actionable statuses always win (Cowork assumption #2).
  if (status === 'attention') return 'needs_you';
  if (status === 'quoted') return 'quoted';
  if (status === 'booked') return 'booked';
  if (status === 'resolved') return 'archived';
  // Otherwise describe the channel rather than say "AI handled".
  return channelPillKey(lastSource, lastDirection);
}

export function pillFor(
  status: ConversationStatus,
  lastSource?: MessageSource,
  lastDirection?: MessageDirection,
): StatusPillConfig {
  return STATUS_PILL_MAP[statusToPillKey(status, lastSource, lastDirection)];
}
